import type { Era, Building, Resources, ResourceType } from './types';
import { BUILDINGS_MEDIEVAL, BUILDINGS_MODERN, BUILDINGS_INDUSTRIAL, BUILDINGS_CONTEMPORARY, BUILDINGS_FUTURE } from './era-buildings';

export const RESOURCE_TYPES: readonly ResourceType[] = ['gold', 'food', 'faith', 'knowledge'] as const;

export const INITIAL_RESOURCES: Resources = {
  gold: 0,
  food: 0,
  faith: 0,
  knowledge: 0,
};

export const OFFLINE_MAX_SECONDS = 8 * 60 * 60;

export const RAIDS_PER_DAY = 3;

export const DAILY_TASKS_COUNT = 3;

export const STREAK_FREEZE_COST_GEMS = 50;

// === ERAS ===

export const ERAS: readonly Era[] = [
  { id: 'ancient',      name: 'Antique',     order: 1, prestigeMultiplier: 2,  wonderName: 'Pyramide',        productionThreshold: 1000 },
  { id: 'medieval',     name: 'Médiéval',    order: 2, prestigeMultiplier: 3,  wonderName: 'Cathédrale',      productionThreshold: 50000 },
  { id: 'modern',       name: 'Renaissance', order: 3, prestigeMultiplier: 5,  wonderName: 'Observatoire',    productionThreshold: 500000 },
  { id: 'industrial',   name: 'Industriel',  order: 4, prestigeMultiplier: 8,  wonderName: 'Usine Titan',     productionThreshold: 5000000 },
  { id: 'contemporary', name: 'Moderne',     order: 5, prestigeMultiplier: 13, wonderName: 'Gratte-Ciel',     productionThreshold: 50000000 },
  { id: 'future',       name: 'Futur',       order: 6, prestigeMultiplier: 21, wonderName: 'Nexus Quantique', productionThreshold: 500000000 },
];

// === BUILDINGS — ANCIENT ERA ===

export const BUILDINGS_ANCIENT: readonly Building[] = [
  // Gold
  { id: 'mine',     name: 'Mine',         description: "Extrait de l'or",        resource: 'gold',      baseCost: { gold: 10, food: 0, faith: 0, knowledge: 0 },        baseProduction: 1,  costMultiplier: 1.15, level: 0, maxLevel: 100 },
  { id: 'market',   name: 'Marché',       description: "Commerce d'or",          resource: 'gold',      baseCost: { gold: 100, food: 50, faith: 0, knowledge: 0 },      baseProduction: 5,  costMultiplier: 1.18, level: 0, maxLevel: 100 },
  { id: 'treasury', name: 'Trésorerie',   description: 'Stocke et multiplie',    resource: 'gold',      baseCost: { gold: 1000, food: 200, faith: 100, knowledge: 0 },  baseProduction: 25, costMultiplier: 1.20, level: 0, maxLevel: 100 },
  // Food
  { id: 'farm',    name: 'Ferme',         description: 'Produit la nourriture',  resource: 'food',      baseCost: { gold: 10, food: 0, faith: 0, knowledge: 0 },        baseProduction: 1,  costMultiplier: 1.15, level: 0, maxLevel: 100 },
  { id: 'mill',    name: 'Moulin',        description: 'Transforme le grain',    resource: 'food',      baseCost: { gold: 50, food: 100, faith: 0, knowledge: 0 },      baseProduction: 5,  costMultiplier: 1.18, level: 0, maxLevel: 100 },
  { id: 'granary', name: 'Grenier',       description: 'Stocke la nourriture',   resource: 'food',      baseCost: { gold: 200, food: 1000, faith: 0, knowledge: 100 },  baseProduction: 25, costMultiplier: 1.20, level: 0, maxLevel: 100 },
  // Faith
  { id: 'shrine', name: 'Sanctuaire',     description: 'Prière basique',         resource: 'faith',     baseCost: { gold: 15, food: 10, faith: 0, knowledge: 0 },       baseProduction: 1,  costMultiplier: 1.15, level: 0, maxLevel: 100 },
  { id: 'temple', name: 'Temple',         description: 'Culte organisé',         resource: 'faith',     baseCost: { gold: 100, food: 100, faith: 100, knowledge: 0 },   baseProduction: 5,  costMultiplier: 1.18, level: 0, maxLevel: 100 },
  { id: 'oracle', name: 'Oracle',         description: 'Prophéties divines',     resource: 'faith',     baseCost: { gold: 500, food: 300, faith: 1000, knowledge: 200 },baseProduction: 25, costMultiplier: 1.20, level: 0, maxLevel: 100 },
  // Knowledge
  { id: 'school',  name: 'École',         description: 'Enseignement basique',   resource: 'knowledge', baseCost: { gold: 20, food: 15, faith: 0, knowledge: 0 },       baseProduction: 1,  costMultiplier: 1.15, level: 0, maxLevel: 100 },
  { id: 'library', name: 'Bibliothèque',  description: 'Savoir accumulé',        resource: 'knowledge', baseCost: { gold: 150, food: 100, faith: 50, knowledge: 100 },  baseProduction: 5,  costMultiplier: 1.18, level: 0, maxLevel: 100 },
  { id: 'academy', name: 'Académie',      description: 'Recherche avancée',      resource: 'knowledge', baseCost: { gold: 500, food: 300, faith: 200, knowledge: 1000 },baseProduction: 25, costMultiplier: 1.20, level: 0, maxLevel: 100 },
];

// === HELPERS ===

export function getEra(eraId: string): Era {
  const era = ERAS.find(e => e.id === eraId);
  if (!era) throw new Error(`Unknown era: ${eraId}`);
  return era;
}

export function getBuildingsByEra(eraId: string): readonly Building[] {
  switch (eraId) {
    case 'medieval':     return BUILDINGS_MEDIEVAL;
    case 'modern':       return BUILDINGS_MODERN;
    case 'industrial':   return BUILDINGS_INDUSTRIAL;
    case 'contemporary': return BUILDINGS_CONTEMPORARY;
    case 'future':       return BUILDINGS_FUTURE;
    default:             return BUILDINGS_ANCIENT;
  }
}
